import React from 'react';
import { FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';
import { translateKeyLang } from '../../../../common/src/translate/translateHelper';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchGet, fetchPost } from '../../store/fetch';
import { materialList } from '../../utils/data/materialList';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { appContext } from '../appContext';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelInput,
    EditPanelRow,
    EditPanelSelect,
    EditPanelSwitch,
    Panel,
    PanelBody,
    PanelBodyHeading,
    PanelButtonLink,
    PanelWithoutForm,
    PopupElement,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { NullableBoolean } from '../utils/NullableBoolean';
import { UnitOfMeasurement } from '../utils/UnitOfMeasurement';

interface IDefaultMaintenanceMeasuresListProps extends ITranslateProps, IRouterProps {
    location: any;
}

interface IValueText {
    value: string;
    text: string;
}

interface IDefaultMaintenanceMeasure {
    id?: undefined | string;
    description?: undefined | string;
    isActive?: undefined | boolean;
    material?: undefined | string;
    maintenanceType?: undefined | string;
    cycle?: undefined | number;
    unitPrice?: undefined | number;
    quantityUnitOfMeasurement?: undefined | string;
    clientCompanyId?: undefined | string;
    clientCompany?:
        | undefined
        | {
              id: string;
              name: string;
          };
}

interface IDefaultMaintenanceMeasuresListState extends IDefaultMaintenanceMeasure {
    isSubmitting: boolean;
    isShowPopup: boolean;
    items: IDefaultMaintenanceMeasure[];
    clientCompanies: IValueText[];
}

class DefaultMaintenanceMeasures extends React.Component<IDefaultMaintenanceMeasuresListProps, IDefaultMaintenanceMeasuresListState> {
    constructor(props: IDefaultMaintenanceMeasuresListProps) {
        super(props);

        this.state = {
            isSubmitting: false,
            isShowPopup: false,
            items: [],
            clientCompanies: [],
        };
    }

    public override async componentDidMount() {
        await this.search();
        void this.loadClientCompanies();
    }

    public override render() {
        return (
            <appContext.Consumer>
                {(appState) => (
                    <>
                        <Topbar>
                            <TopbarLeft>
                                <TopbarCrumbActive
                                    title={`${translateKeyLang(appState.languageCode, 'maintenanceMeasure.default')} ${
                                        this.props.location.query.unitname
                                    }`}
                                    icon="fa-cogs"
                                />
                                <TopbarCrumb>
                                    <Link to="/settings/unitcodes">{translateKeyLang(appState.languageCode, 'common.units')}</Link>
                                </TopbarCrumb>
                            </TopbarLeft>
                            <TopbarRight>
                                <PanelButtonLink
                                    onClick={() => this.handleAddMeasurementClicked()}
                                    small={true}
                                    text={translateKeyLang(
                                        appState.languageCode,
                                        'common.addItem',
                                        translateKeyLang(appState.languageCode, 'maintenanceMeasure.maintenanceMeasure'),
                                    )}
                                    icon="fa fa-plus"
                                />
                            </TopbarRight>
                        </Topbar>

                        <Content>
                            <Tray>
                                <Panel>
                                    {(this.state.isSubmitting && <Spinner />) || (
                                        <PanelBody noPadding={true}>
                                            <Table>
                                                <TableHead>
                                                    <TableHeadRow light={true}>
                                                        <TableHeader>{translateKeyLang(appState.languageCode, 'common.name')}</TableHeader>
                                                        <TableHeader>{translateKeyLang(appState.languageCode, 'common.isActive')}</TableHeader>
                                                        <TableHeader>{translateKeyLang(appState.languageCode, 'common.material')}</TableHeader>
                                                        <TableHeader>
                                                            {translateKeyLang(appState.languageCode, 'maintenanceMeasure.cycle')}
                                                        </TableHeader>
                                                        <TableHeader>
                                                            {translateKeyLang(appState.languageCode, 'maintenanceMeasure.unitPrice')}
                                                        </TableHeader>
                                                        {appState.userRole === Roles.Administrator && (
                                                            <>
                                                                <TableHeader>{translateKeyLang(appState.languageCode, 'common.company')}</TableHeader>
                                                            </>
                                                        )}
                                                        <TableHeader>&nbsp;</TableHeader>
                                                    </TableHeadRow>
                                                </TableHead>
                                                <TableBody>
                                                    {this.state.items &&
                                                        this.state.items.map((item) => (
                                                            <TableRow key={item.id}>
                                                                <TableData>{item.description}</TableData>
                                                                <TableData>
                                                                    <NullableBoolean value={item.isActive} useSymbols={true} />
                                                                </TableData>
                                                                <TableData>{item.material}</TableData>
                                                                <TableData>
                                                                    <FormattedNumber value={item.cycle ?? 0} />
                                                                </TableData>
                                                                <TableData>
                                                                    <FormattedNumber value={item.unitPrice ?? 0} style="currency" currency="EUR" /> /{' '}
                                                                    <UnitOfMeasurement value={item.quantityUnitOfMeasurement} />
                                                                </TableData>
                                                                {appState.userRole === Roles.Administrator && (
                                                                    <>
                                                                        <TableData>{item.clientCompany?.name}</TableData>
                                                                    </>
                                                                )}
                                                                <TableData alignRight={true}>
                                                                    <IconEditButton onClick={() => this.handleEditMeasurementClicked(item.id!)} />
                                                                </TableData>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </PanelBody>
                                    )}
                                    {this.state.isShowPopup && (
                                        <>
                                            <PopupElement onClick={() => this.setState({ isShowPopup: false })}>
                                                <form method="post" action="/" id="admin-form" onSubmit={async () => this.saveClicked()}>
                                                    <PanelWithoutForm>
                                                        <PanelBodyHeading
                                                            text={translateKeyLang(
                                                                appState.languageCode,
                                                                'common.addItem',
                                                                translateKeyLang(appState.languageCode, 'maintenanceMeasure.maintenanceMeasure'),
                                                            )}
                                                        >
                                                            <EditPanelRow>
                                                                <EditPanelCell rowCount={8}>
                                                                    <EditPanelInput
                                                                        name="description"
                                                                        placeholder={translateKeyLang(
                                                                            appState.languageCode,
                                                                            'maintenanceMeasure.maintenanceMeasure',
                                                                        )}
                                                                        value={this.state.description}
                                                                        icon="fa fa-pagelines"
                                                                        handleChange={this.handleChange.bind(this)}
                                                                    />
                                                                </EditPanelCell>
                                                                <EditPanelCell rowCount={4}>
                                                                    {(!isCustomerVersion('ronik') && (
                                                                        <EditPanelSelect
                                                                            name="material"
                                                                            value={this.state.material}
                                                                            handleChange={this.handleChange.bind(this)}
                                                                            placeholder={translateKeyLang(appState.languageCode, 'common.material')}
                                                                            options={materialList()}
                                                                        />
                                                                    )) || (
                                                                        <EditPanelInput
                                                                            name="material"
                                                                            placeholder={translateKeyLang(appState.languageCode, 'common.material')}
                                                                            value={this.state.material}
                                                                            icon="fa fa-flask"
                                                                            handleChange={this.handleChange.bind(this)}
                                                                        />
                                                                    )}
                                                                </EditPanelCell>
                                                            </EditPanelRow>
                                                            <EditPanelRow>
                                                                <EditPanelCell rowCount={4}>
                                                                    <EditPanelSelect
                                                                        name="maintenanceType"
                                                                        value={this.state.maintenanceType}
                                                                        handleChange={this.handleChange.bind(this)}
                                                                        options={[
                                                                            { value: 'Dagelijksonderhoud', text: 'Dagelijksonderhoud' },
                                                                            { value: 'Groot onderhoud', text: 'Groot onderhoud' },
                                                                            { value: 'Vervangen', text: 'Vervangen' },
                                                                        ]}
                                                                    />
                                                                </EditPanelCell>
                                                                <EditPanelCell rowCount={2}>
                                                                    <EditPanelInput
                                                                        name="cycle"
                                                                        placeholder={translateKeyLang(
                                                                            appState.languageCode,
                                                                            'maintenanceMeasure.cycle',
                                                                        )}
                                                                        value={this.state.cycle}
                                                                        handleChange={this.handleChange.bind(this)}
                                                                    />
                                                                </EditPanelCell>
                                                                <EditPanelCell rowCount={2}>
                                                                    <EditPanelInput
                                                                        name="unitPrice"
                                                                        placeholder={translateKeyLang(
                                                                            appState.languageCode,
                                                                            'maintenanceMeasure.unitPrice',
                                                                        )}
                                                                        value={this.state.unitPrice}
                                                                        handleChange={this.handleChange.bind(this)}
                                                                    />
                                                                </EditPanelCell>
                                                                <EditPanelCell rowCount={4}>
                                                                    <EditPanelSelect
                                                                        name="quantityUnitOfMeasurement"
                                                                        value={this.state.quantityUnitOfMeasurement}
                                                                        handleChange={this.handleChange.bind(this)}
                                                                        options={[
                                                                            { value: 'm', text: 'm' },
                                                                            { value: 'm2', text: 'm2' },
                                                                            { value: 'm3', text: 'm3' },
                                                                            {
                                                                                value: 'pcs',
                                                                                text: translateKeyLang(appState.languageCode, 'common.pcs'),
                                                                            },
                                                                        ]}
                                                                    />
                                                                </EditPanelCell>
                                                            </EditPanelRow>
                                                            <EditPanelRow>
                                                                <EditPanelCell rowCount={6}>
                                                                    <EditPanelSwitch
                                                                        name="isActive"
                                                                        placeholder={translateKeyLang(appState.languageCode, 'common.isActive')}
                                                                        value={this.state.isActive}
                                                                        handleChange={this.handleChange.bind(this)}
                                                                    />
                                                                </EditPanelCell>
                                                                {appState.userRole === Roles.Administrator && (
                                                                    <EditPanelCell rowCount={6}>
                                                                        <EditPanelSelect
                                                                            name="clientCompanyId"
                                                                            placeholder={translateKeyLang(
                                                                                appState.languageCode,
                                                                                'common.selectItem',
                                                                                translateKeyLang(appState.languageCode, 'common.client'),
                                                                            )}
                                                                            value={this.state.clientCompanyId}
                                                                            handleChange={this.handleChange.bind(this)}
                                                                            options={this.state.clientCompanies}
                                                                        />
                                                                    </EditPanelCell>
                                                                )}
                                                            </EditPanelRow>
                                                            <EditPanelButtons>
                                                                <EditPanelButton
                                                                    onClick={this.handleCloseFormClicked.bind(this)}
                                                                    small={true}
                                                                    text={translateKeyLang(appState.languageCode, 'common.cancel')}
                                                                />
                                                                <EditPanelButton
                                                                    onClick={this.saveClicked.bind(this)}
                                                                    small={true}
                                                                    primary={true}
                                                                    disabled={this.state.isSubmitting}
                                                                    text={translateKeyLang(
                                                                        appState.languageCode,
                                                                        'common.saveItem',
                                                                        translateKeyLang(
                                                                            appState.languageCode,
                                                                            'maintenanceMeasure.maintenanceMeasure',
                                                                        ),
                                                                    )}
                                                                />
                                                            </EditPanelButtons>
                                                        </PanelBodyHeading>
                                                    </PanelWithoutForm>
                                                </form>
                                            </PopupElement>
                                        </>
                                    )}
                                </Panel>
                            </Tray>
                        </Content>
                    </>
                )}
            </appContext.Consumer>
        );
    }

    private handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        this.setState(newState);
    }

    private async saveClicked() {
        this.setState({ isSubmitting: true });
        const data = {
            objectTypeUnitCodeId: this.props.match.params.id,
            description: this.state.description,
            material: this.state.material,
            maintenanceType: this.state.maintenanceType,
            cycle: this.state.cycle!.toString().replace(',', '.'),
            unitPrice: this.state.unitPrice!.toString().replace(',', '.'),
            quantityUnitOfMeasurement: this.state.quantityUnitOfMeasurement,
            clientCompanyId: this.state.clientCompanyId,
            isActive: this.state.isActive,
        };

        let url = '/api/defaultMaintenanceMeasures';
        if (this.state.id) {
            url += `/${this.state.id}`;
        }
        await fetchPost(url, {}, data);
        await this.search();
        this.setState({ isShowPopup: false, id: undefined });
    }

    private handleAddMeasurementClicked() {
        this.setState({
            isShowPopup: true,
            isActive: true,
            description: undefined,
            material: ' ',
            maintenanceType: 'Dagelijksonderhoud',
            cycle: 0,
            unitPrice: 0,
            quantityUnitOfMeasurement: 'm',
            clientCompanyId: undefined,
            id: undefined,
        });
    }

    private handleEditMeasurementClicked(id: string) {
        const item = this.getItemFromState(id);

        if (item) {
            this.setState({
                isShowPopup: true,
                id,
                description: item.description,
                material: item.material,
                maintenanceType: item.maintenanceType,
                cycle: item.cycle,
                unitPrice: item.unitPrice,
                isActive: item.isActive,
                quantityUnitOfMeasurement: item.quantityUnitOfMeasurement ?? 'm',
                clientCompanyId: item.clientCompany?.id,
            });
        }
    }

    private handleCloseFormClicked() {
        this.setState({ isShowPopup: false });
    }

    private getItemFromState(id: string) {
        for (let i = 0; i < this.state.items.length; i++) {
            const item = this.state.items[i]!;
            if (item.id === id) {
                return item;
            }
        }
        return undefined;
    }

    private async search() {
        const url = '/api/defaultMaintenanceMeasures';
        this.setState({ isSubmitting: true });
        const result = await fetchGet(url, { objectTypeUnitCodeId: this.props.match.params.id });
        this.setState({ items: result.json, isSubmitting: false });
    }

    private async loadClientCompanies() {
        const url = '/api/clients';
        const result = await fetchGet(url, {});
        const options = result.json.items.map((item: any) => ({ value: item.id, text: item.name }));
        this.setState({ clientCompanies: options });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(DefaultMaintenanceMeasures);
